<template>
  <LoadingSpinner v-if="isLoading" />

  <div
    v-else
    class="show-proposal no-print"
  >
    <ProjectHeader :project="project">
      <template #actions>
        <BaseButton v-if="proposal.id" :to="{ name: 'EditProposal', params: { id: proposal.id } }" variant="accent">Edit Proposal</BaseButton>
        <BaseButton v-else :to="{ name: 'CreateProposal', params: { id: project.id } }" variant="accent">Create Proposal</BaseButton>
      </template>
    </ProjectHeader>
    <ProjectSubNavigation :project="project" />

    <!-- Proposal -->
    <div v-if="proposal.id" class="no-print">
      <!-- actions -->
      <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-7">
        <!-- show view count and last viewed date -->
        <div class="col-span-1 sm:col-span-3 flex sm:flex-row justify-around sm:justify-evenly items-center">
          <div class="flex flex-col gap-y-1 text-center font-medium mb-4 sm:mb-0">
            <span class="text-sm leading-6 text-slate-500">Views</span>
            <span>{{ proposal.viewCount || '-' }}</span>
          </div>

          <div class="flex flex-col gap-y-1 text-center font-medium mb-4 sm:mb-0">
            <span class="text-sm leading-6 text-slate-500">Last Viewed</span>
            <span>{{ proposal.lastViewed ? displayDate(new Date(proposal.lastViewed).getTime()/1000) : '#' }}</span>
          </div>
        </div>

        <!-- Manage -->
        <div class="flex justify-end items-center gap-x-4">
          <BaseButton
            varient="base"
            icon-class="i-mdi-eye"
            text="Preview"
            target="_blank"
            :to="{ name: 'CustomerViewProposal', params: { id: proposal.id }, query: { preview: 'true' } }"
          />

          <BaseDropdown
            title="Manage"
            right
            :items="[
              { title: 'Edit Proposal', disabled: isAccecptedOrArchived, to: { name: 'EditProposal', params: { id: proposal.id } } },
              { title: 'Print', action: printProposal },
              { title: 'Email', action: emailProposal },
              { title: 'Accept for customer', disabled: denyDeclineOrAccpet, action: acceptProposal },
              { title: 'Decline for customer', disabled: denyDeclineOrAccpet, action: declineProposal },
              { title: 'Set To Awaiting Acceptance', disabled: denyDelete, action: setToAwaitingAcceptance },
              { title: 'Undo Acceptance', disabled: denyUndo, action: undoAcceptance },
              { title: 'Delete', disabled: denyDelete, action: () => showProposalDeleteConfirmation = true },
            ]"
          />
        </div>
      </div>

      <!-- Proposal details -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-2 gap-y-4 md:gap-y-0 mt-11">
        <div class="border-b md:border-b-0 md:border-r border-gray-300 pb-4 md:pb-0">
          <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2 ml-2">Status</h3>
          <ProposalStatusBadge :status="proposal.status" class="mx-2" />
        </div>

        <div class="border-b md:border-b-0 md:border-r border-gray-300 pb-4 md:pb-0">
          <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2 ml-2">Proposal #</h3>
          <span class="text-lg font-medium leading-5 tracking-tight ml-2">{{ proposal.contractId }}</span>
        </div>

        <div class="border-b md:border-b-0 md:border-r border-gray-300 pb-4 md:pb-0">
          <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2 ml-2">Date</h3>
          <span class="text-lg font-medium leading-5 tracking-tight ml-2">{{ new Date(proposal.contractDate).toLocaleDateString() }}</span>
        </div>

        <div class="border-b md:border-b-0 md:border-r border-gray-300 pb-4 md:pb-0">
          <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2 ml-2">Valid Until</h3>
          <span class="text-lg font-medium leading-5 tracking-tight ml-2">{{ new Date(proposal.contractValidDate).toLocaleDateString() }}</span>
        </div>

        <div class="border-b md:border-b-0 md:border-r border-gray-300 pb-4 md:pb-0">
          <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2 ml-2">Customer</h3>
          <RouterLink v-if="customer?.id" :to="{ name: 'ShowCustomer', params: { id: customer.id } }">
            <span class="text-lg font-medium leading-5 tracking-tight ml-2">{{ customer.name }}</span>
          </RouterLink>
        </div>

        <div>
          <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2 ml-2">Contract Value</h3>
          <span class="text-lg font-medium leading-5 tracking-tight ml-2">{{ currency(proposalTotalPrice) }}</span>
        </div>
      </div>

      <!-- items -->
      <div class="border-t border-gray-300 pt-6 mt-6">
        <!-- original contract items -->
        <div class="mt-8">
          <div
            v-for="(lineItems, category) in getProposalItems"
            :key="category"
            class="mb-3"
          >
            <div class="mb-2 pb-2 border-b border-slate-200">
              <span class="font-semibold text-xl">{{ category }}</span>
            </div>

            <div v-for="(lineItem) in lineItems" :key="lineItem.id">
              <div>
                <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div class="col-span-1 md:col-span-4 flex items-start">
                    <div v-if="lineItem.optional" class="mr-2 mt-4">
                      <!-- optional checkbox -->
                      <BaseCheckbox v-model="lineItem.selected" label="Optional" :disabled="isAccecptedOrArchived" />
                    </div>

                    <BaseCollapse :bg-color="'bg-withe'">
                      <template #header>
                        <div class="flex items-center">
                          <span class="font-bold">{{ lineItem.title }}</span>
                          <span v-if="lineItem.isCreditLineItem" class="ml-2">
                            <CreditBadge />
                          </span>
                        </div>
                      </template>

                      <div class="text-justify" v-html="lineItem.scopeOfWork" />

                      <!-- attachments -->
                      <LightBox :attachments="lineItem.attachments" />
                    </BaseCollapse>
                  </div>

                  <!-- totals -->
                  <!-- accepted pricing infos -->
                  <div  v-if="isAccecptedOrArchived" class="col-span-1 flex flex-col gap-1 mt-4 text-sm text-left md:text-right">
                    <p class="text-primary-400">{{ decimalFormat(0, 2) }}% invoiced</p>
                    <p>{{ currency(lineItem.totalPrice) }}</p>
                    <p>{{ currency(lineItem.totalPrice) }} balance</p>
                  </div>
                  <!-- Not accepted yet pricing infos -->
                  <div v-else class="col-span-1 flex flex-col gap-1 mt-4 text-sm text-left md:text-right">
                    <p class="text-primary-400">{{ currency(lineItem.cost) }}</p>
                    <p class="text-primary-400">{{ decimalFormat(lineItem.margin, 2) }}% margin</p>
                    <p v-if="lineItem.quantity > 1">{{ currency(lineItem.unitPrice) }}</p>
                    <p v-if="lineItem.quantity > 1">Qty: x {{ lineItem.quantity }}</p>
                    <p>{{ currency(lineItem.totalPrice) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- change orders list -->
        <div
          v-if="changeOrders.length"
          class="mt-11"
        >
          <ChangeOrderList
            :change-orders="changeOrders"
            :budget="budget"
            :proposal="proposal"
            :customer="customer"
            :project="project"
          />
        </div>

        <!-- add change order button -->
        <div
          v-if="proposal.status === PROPOSAL_STATUS.ACCEPTED && !showChangeOrderForm"
          class="my-8 flex justify-center items-center"
        >
          <BaseButton
            type="button"
            style="box-shadow: none !important;"
            @click="showChangeOrderForm = true"
          >
            <span class="i-heroicons-plus-circle size-8" />
            <br>
            <p class="text-lg">Add change order</p>
          </BaseButton>
        </div>

        <!-- Change Order Form -->
        <div
          v-if="showChangeOrderForm && proposal.status === PROPOSAL_STATUS.ACCEPTED"
          class="mb-4"
        >
          <div class="bg-slate-100 p-4 rounded-lg gap-4">
            <ChangeOrderForm
              :customer-id="customer.id"
              :project-id="project.id"
              :proposal="proposal"
              :category-weight="nextCategoryWeight()"
              :default-category-name="defaultCategoryName()"
              :save-function="changeOrderSaved"
              @remove-change-order="hideChangeOrderForm"
            />
          </div>
        </div>
      </div>

      <hr class="border-b-slate-200 border-2" />
      <!-- all totals -->
      <div class="mt-8 ml-0 md:ml-auto min-w-full md:min-w-[30%] max-w-full md:max-w-fit">
        <div class="bg-slate-100 p-4 rounded-lg gap-4 text-left md:text-right">
          <div class="grid grid-cols-2">
            <span class="font-semibold">Contract Value:</span> <span>{{ currency(proposalTotalPrice) }}</span>
          </div>

          <!-- change orders totals -->
          <div
            v-for="(total, category) in getAllChangeOrdersTotal"
            :key="category"
            class="grid grid-cols-2"
          >
            <span class="font-semibold">{{ category }}:</span> <span>{{ currency(total) }}</span>
          </div>

          <div class="grid grid-cols-2">
            <span class="font-semibold">Total:</span> <span>{{ currency(proposalTotalPrice + getChangeOrdersTotal) }}</span>
          </div>
          <div class="grid grid-cols-2">
            <span class="font-semibold">Approved Total:</span> <span>{{ currency(getApprovedTotal) }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- No Proposal -->
    <div v-else class="flex flex-col items-center justify-center mt-12">
      <span class="i-heroicons-folder-open size-10 text-muted" />
      <h2 class="mb-2">No Proposal</h2>
      <p v-if="budget.id" class="mb-4">Get started by creating a new proposal.</p>
      <p v-else class="mb-4">Get started by creating a budget then a proposal.</p>
      <BaseButton
        v-if="budget.id"
        :to="{ name: 'CreateProposal', params: { id: project.id } }"
        varient="primary"
        icon-class="i-heroicons-plus-circle"
        text="New Proposal"
      />
      <BaseButton
        v-else
        :to="{ name: 'CreateBudget', params: { id: project.id } }"
        varient="primary"
        icon-class="i-heroicons-plus-circle"
        text="New Budget"
      />
    </div>
  </div>

  <!-- delete confirmation modal -->
  <Modal
    id="delete-proposal-confirmation-modal"
    v-model="showProposalDeleteConfirmation"
    title="Delete Proposal"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
  >
    <template #body>
      <div class="mt-2">
        <p class="text-base mb-3">Are you sure you want to delete this proposal?</p>
        <p class="text-sm mb-2">This will also delete:</p>
        <ul class="text-sm list-disc ml-6">
          <li>All change orders (if they exist)</li>
        </ul>
      </div>
    </template>

    <template #footer>
      <div class="bg-white py-3 flex px-6 justify-end gap-2">
        <BaseButton
          text="Cancel"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="showProposalDeleteConfirmation = false"
        />

        <BaseButton
          variant="danger"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="deleteProposal()"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Confirm</span>
            <span
              v-if="isDeleting"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>
    </template>
  </Modal>

  <Modal
    id="email-proposal"
    v-model="showEmailModal"
    title="Send Proposal"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    width="md:w-[46rem] w-full"
  >
    <ProposalEmail
      :proposal="proposal"
      :project="project"
      :email="(email as Email)"
      @close="showEmailModal = false"
    />
  </Modal>

  <div
    v-if="!isLoading && proposal.id"
    id="printContent"
    class="hide-print-content"
  >
    <PrintProposal
      :project="project"
      :customer="customer"
      :budget="budget"
      :proposal="proposal"
    />
  </div>
</template>

<script lang="ts" setup>
import BaseButton from '@/components/base/BaseButton.vue'
import { filter, contains, extend } from 'underscore'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import { Budget, BudgetItem } from '@/models/budget'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import BaseCollapse from '@/components/base/BaseCollapse.vue'
import ChangeOrderForm from '../components/ChangeOrderForm.vue'
import ProposalEmail from '../components/ProposalEmail.vue'
import PrintProposal from '../components/PrintProposal.vue'
import LightBox from '@/components/app/AppLightBox.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { currency, displayDate, sortLineItemsByCategoryName, toNumber, decimalFormat } from '@/utils'
import { useHead } from '@unhead/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'
import BudgetService from '@/services/BudgetService'
import ProjectService from '@/services/ProjectService'
import CustomerService from '@/services/CustomerService'
import ProposalService from '@/services/ProposalService'
import { computed, ref } from 'vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import ProposalStatusBadge from '../components/ProposalStatusBadge.vue'
import { useNotification } from '@kyvg/vue3-notification'
import Modal from '@/components/app/AppModal.vue'
import { PROJECT_STATUS } from '@/models/project'
import ChangeOrderList from '../components/ChangeOrdersList.vue'
import { Email } from '@/models/email'
import EmailService, { EmailTemplate } from '@/services/EmailService'
import Handlebars from 'handlebars'
import { Customer } from '@/models/customer'
import { useAuth } from '@/composables/useAuth'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import CreditBadge from '@/components/base/CreditBadge.vue'

useHead({ title: 'Project proposal' })
const $route = useRoute()
const budgetService = new BudgetService()
const projectService = new ProjectService()
const customerService = new CustomerService()
const proposalService = new ProposalService()
const queryClient = useQueryClient()
const { notify } = useNotification()

const proposalId = $route.params.id as string || ''

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', proposalId],
  queryFn: async () => projectService.find(proposalId),
  enabled: !!proposalId && proposalId !== ''
})

const { data: budget, isLoading: budgetLoading } = useQuery({
  queryKey: ['budget', proposalId],
  queryFn: async () =>  {
    const budget = await budgetService.find(proposalId) || new Budget()
    const lineItems = await budgetService.getLineItems(proposalId)
    budget.lineItems = lineItems
    return budget
  },
  enabled: !!proposalId && proposalId !== ''
})

const { data: proposal, isLoading: proposalLoading } = useQuery({
  queryKey: ['proposal', proposalId],
  queryFn: async () => {
    const proposal = await proposalService.find(proposalId) || new Proposal()
    return proposal
  },
  enabled: !!proposalId && proposalId !== ''
})

const { data: changeOrders, isLoading: changeOrdersLoading } = useQuery({
  queryKey: ['changeOrders', proposalId],
  queryFn: async () => await proposalService.getChangeOrders(proposalId) as Proposal[],
  enabled: !!proposalId && proposalId !== ''
})

const { data: customer, isLoading: customerLoading } = useQuery({
  queryKey: ['customer', proposal.value?.customerId],
  queryFn: () => customerService.find(proposal.value?.customerId) || new Customer(),
  enabled: computed(() => !!proposal.value?.customerId)
})

const { data: realtor, isLoading: realtorLoading } = useQuery({
  queryKey: ['realtor', project.value?.realtorId],
  queryFn: () => customerService.find(project.value?.realtorId),
  enabled: computed(() => !!project.value?.realtorId)
})

const showChangeOrderForm = ref(false)

const sortCategories = computed(() => {
  const categories = budget.value.lineItems.map((item) => item.category)
  return categories.filter((category, index) => categories.findIndex((c) => c.name === category.name) === index).sort((a, b) => a.weight - b.weight)
})
// find the highest weight and add 1
const nextCategoryWeight = () => {
  return (
    Math.max(...sortCategories.value.map((category) => category.weight)) + 1
  )
}

const isLoading = computed(() => projectLoading.value || budgetLoading.value || proposalLoading.value || changeOrdersLoading.value || customerLoading.value || realtorLoading.value)

const getProposalItems = computed(() => {
  const items = filter(budget.value.lineItems, (item) => contains(proposal.value.lineItems, item.id))
  const sortedItems = sortLineItemsByCategoryName(items)

  return sortedItems
})

const isAccecptedOrArchived = computed(() => proposal.value.status === PROPOSAL_STATUS.ACCEPTED || proposal.value.status === PROPOSAL_STATUS.ARCHIVED)
const denyDeclineOrAccpet = computed(() => proposal.value.status === PROPOSAL_STATUS.DECLINED || proposal.value.status === PROPOSAL_STATUS.ARCHIVED || proposal.value.status === PROPOSAL_STATUS.ACCEPTED)
const denyUndo = computed(()=> proposal.value.status === PROPOSAL_STATUS.DRAFT || proposal.value.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE || proposal.value.status === PROPOSAL_STATUS.ARCHIVED || changeOrders.value.length > 0)
const denyDelete = computed(()=> proposal.value.status === PROPOSAL_STATUS.DECLINED || proposal.value.status === PROPOSAL_STATUS.ARCHIVED || proposal.value.status === PROPOSAL_STATUS.ACCEPTED || proposal.value.status == PROPOSAL_STATUS.AWAITING_ACCEPTANCE)

const proposalTotalPrice = computed(() => {
  return budget.value.lineItems
    .filter(item => proposal.value.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})

const getChangeOrdersTotal = computed(() => {
  return changeOrders.value.reduce((total, changeOrder) => {
    return total + budget.value.lineItems
      .filter(item => changeOrder.lineItems.includes(item.id) && item.selected)
      .reduce((subtotal, item) => subtotal + toNumber(item.totalPrice), 0)
  }, 0)
})

const getChangeOrderTotal = (lineItems: BudgetItem[]) => {
  return lineItems
    .filter(lineItem => lineItem.selected)
    .reduce((total, lineItem) => total + toNumber(lineItem.totalPrice), 0)
}

const getApprovedTotal = computed(() => {
  if (proposal.value.status !== PROPOSAL_STATUS.ACCEPTED) {
    return 0
  }

  let total = proposalTotalPrice.value

  changeOrders.value.forEach((changeOrder: Proposal) => {
    if (changeOrder.status === PROPOSAL_STATUS.ACCEPTED) {
      const lineItems = budget.value.lineItems.filter(item => changeOrder.lineItems.includes(item.id))
      total += getChangeOrderTotal(lineItems)
    }
  })

  return total
})

const getAllChangeOrdersTotal = computed(() => {
  return changeOrders.value.reduce((totals, changeOrder) => {
    const lineItems = budget.value.lineItems.filter(item => changeOrder.lineItems.includes(item.id))

    if (changeOrder.status !== PROPOSAL_STATUS.DECLINED) {
      return { ...totals, [lineItems[0].category.name]: getChangeOrderTotal(lineItems) }
    } else {
      return { ...totals, [lineItems[0].category.name]: lineItems.reduce((total, lineItem) => total + toNumber(lineItem.totalPrice), 0) }
    }
  }, {})
})

const hideChangeOrderForm = () => {
  showChangeOrderForm.value = false
}

const defaultCategoryName = () => {
  const name = 'Untitled Category'
  const categoriesNames = sortCategories.value.map((category) => category.name)
  let i = 1
  let newName = name

  while (categoriesNames.includes(newName)) {
    newName = `${name} ${i}`
    i++
  }

  return newName
}

const { mutateAsync: changeOrderSaved } = useMutation({
  mutationFn: async (payload: {proposal: Proposal, lineItems: BudgetItem[]}) => {
    // create new line items
    const allPromises = payload.lineItems.map((lineItem) => budgetService.addLineItem(budget.value.id, lineItem))
    const updatedLineItems = await Promise.all(allPromises)

    // create change order
    payload.proposal.lineItems = updatedLineItems.map((lineItem) => lineItem.id)
    const createdChangeOrder = await proposalService.addChangeOrder(proposal.value.id, payload.proposal)

    return { createdChangeOrder, updatedLineItems }
  },
  onSuccess: ({ createdChangeOrder, updatedLineItems }) => {
    queryClient.setQueryData(['changeOrders', proposalId], [...changeOrders.value, createdChangeOrder])
    queryClient.setQueryData(['budget', budget.value.id], {...budget.value, lineItems: [...budget.value.lineItems, ...updatedLineItems]})

    notify({
      type: 'success',
      title: 'Change Order Created',
      text: 'Change Order created successfully'
    })

    hideChangeOrderForm()
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error creating change order',
      text: error.message || 'An error occurred while creating the change order'
    })
  }
})

const { currentUser } = useAuth()

const { mutateAsync: acceptProposal } = useMutation({
  mutationFn: async () =>  {
    const updatedP = extend(new Proposal(), {...proposal.value, status: PROPOSAL_STATUS.ACCEPTED }) as Proposal
    updatedP.signature = {
      name: currentUser.value.name,
      email: currentUser.value.email || '',
      timestamp: new Date(),
      consent: false,
      ipAddress: '',
      signature: '',
      signatureImage: '',
      date: new Date()
    }
    return await proposalService.update(proposal.value.id, updatedP)
  },
  onSuccess: (updatedProposal) => {
    queryClient.setQueryData(['proposal', proposalId], updatedProposal)
    queryClient.setQueryData(['project', proposalId], {...project.value, status: PROJECT_STATUS.IN_PROGRESS })

    notify({
      type: 'success',
      title: 'Proposal Accepted',
      text: 'Proposal accepted successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error accepting proposal',
      text: error.message || 'An error occurred while accepting the proposal'
    })
  }
})

const { mutateAsync: declineProposal } = useMutation({
  mutationFn: async () =>  await proposalService.update(proposal.value.id, {...proposal.value, status: PROPOSAL_STATUS.DECLINED }),
  onSuccess: (updatedProposal) => {
    queryClient.setQueryData(['proposal', proposalId], updatedProposal)
    queryClient.setQueryData(['project', proposalId], {...project.value, status: PROJECT_STATUS.LOST })

    notify({
      type: 'success',
      title: 'Proposal Declined',
      text: 'Proposal declined successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error declining proposal',
      text: error.message || 'An error occurred while declining the proposal'
    })
  }
})

const { mutateAsync: undoAcceptance } = useMutation({
  mutationFn: async () =>  {
    // update budget line items
    let updatedBudget = extend(new Budget(), budget.value) as Budget
    updatedBudget.lineItems = budget.value.lineItems.map((item) => {
      const lineItem = extend(new BudgetItem(), item) as BudgetItem
      lineItem.selected = true
      lineItem.optional = false
      return lineItem
    })

    updatedBudget = await budgetService.updateBudget(updatedBudget, [])
    queryClient.setQueryData(['budget', updatedBudget.id], updatedBudget)

    return await proposalService.update(proposal.value.id, {...proposal.value, lineItems: [...updatedBudget.lineItems.map((item) => item.id)], status: PROPOSAL_STATUS.DRAFT })
  },
  onSuccess: (updatedProposal) => {
    queryClient.setQueryData(['proposal', proposalId], updatedProposal)
    queryClient.setQueryData(['project', proposalId], {...project.value, status: PROJECT_STATUS.ESTIMATING })

    notify({
      type: 'success',
      title: 'Proposal Undo Acceptance',
      text: 'Proposal acceptance undone successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error undo acceptance',
      text: error.message || 'An error occurred while undoing the acceptance'
    })
  }
})

const {mutateAsync: setToAwaitingAcceptance } = useMutation({
  mutationFn: async () =>  await proposalService.update(proposal.value.id, {...proposal.value, status: PROPOSAL_STATUS.AWAITING_ACCEPTANCE }),
  onSuccess: (updatedProposal) => {
    queryClient.setQueryData(['proposal', proposalId], updatedProposal)
    queryClient.setQueryData(['project', proposalId], {...project.value, status: PROJECT_STATUS.AWAITING_ACCEPTANCE })

    notify({
      type: 'success',
      title: 'Proposal Set To Awaiting Acceptance',
      text: 'Proposal set to awaiting acceptance successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error setting to awaiting acceptance',
      text: error.message || 'An error occurred while setting to awaiting acceptance'
    })
  }
})

const showProposalDeleteConfirmation = ref(false)

const { mutateAsync: deleteProposal, isPending: isDeleting } = useMutation({
  mutationFn: async () =>  {
    await proposalService.deleteProposal(proposal.value.id)
    await customerService.updateProject(project.value.customerId, project.value.id, { ...project.value, status: PROJECT_STATUS.ESTIMATING })
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] })
    queryClient.invalidateQueries({ queryKey: ['project', proposalId] })

    notify({
      type: 'success',
      title: 'Proposal Deleted',
      text: 'Proposal deleted successfully'
    })

    showProposalDeleteConfirmation.value = false
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error deleting proposal',
      text: error.message || 'An error occurred while deleting the proposal'
    })
  }
})

const showEmailModal = ref(false)

const emailProposal = () => {
  const link = `${window.location.origin}/proposal/${project.value.id}`
  const emailData = { customerName: customer.value.name, link: link }

  email.value.to = [customer.value.email]
  email.value.cc = []
  if (realtor.value?.email) {
    email.value.cc.push(realtor.value.email)
  }
  email.value.message.subject = Handlebars.compile(emailTemplate.value.subject).call(this, { projectName: project.value.name })

  Handlebars.registerPartial(baseTempalte.value.name, baseTempalte.value.html)
  email.value.message.html = Handlebars.compile(emailTemplate.value.html).call(this, emailData)
  showEmailModal.value = true
}

const email = ref({...new Email(), to: [], cc: []})
const emailService = new EmailService()

const { data: baseTempalte } = useQuery({
  queryKey: ['emailTemplates/base_layout'],
  queryFn: async () => {
    return await emailService.find('base_layout') || new EmailTemplate()
  }
})

const { data: emailTemplate } = useQuery({
  queryKey: ['emailTemplates/send_proposal'],
  queryFn: async () => {
    return await emailService.find('send_proposal') || new EmailTemplate()
  }
})


const printProposal = () => {
  window.print()
}
</script>

<style scoped>
.not-selected-item {
  opacity: 0.7;
}
@media print {
  .no-print {
    display: none;
  }
  #printContent {
    display: block;
  }
}

.hide-print-content {
  display: none;
}
</style>
